import React, { useState, useEffect } from 'react';
import { useAppContext } from 'components/Context/AppContext';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { setCurrency } from 'utils/functionality';
import { Line } from 'react-chartjs-2';
import { Button, PaginationTable, Table, TableWrapper } from 'components/Global';
import { DateFilter } from './DateFilter';
import { exportUserLoyaltyPointDashboard, getLoyaltyPointDashboard } from 'features/dashboard-point/actions';
import LoadingSpinner from 'components/Global/LoadingSpinner';
import { ReactComponent as PointIcon } from 'icons/point-filled-icon.svg';
import { ReactComponent as PointGreenIcon } from 'icons/point-filled-green-icon.svg';
import { ReactComponent as PointOrangeIcon } from 'icons/point-filled-orange-icon.svg';
import { ReactComponent as PeopleIcon } from 'icons/people-filled-icon.svg';
import { ReactComponent as DownloadIcon } from 'icons/download-excel.svg';

const column = [
  { header: 'No.', value: 'no' },
  { header: 'Nama Lengkap', value: 'name' },
  { header: 'Email', value: 'email' },
  { header: 'Total Point', value: 'total_earned' },
  { header: 'Loyalty Level', value: 'loyalty_experience_level' },
];

const DashboardPointComponent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { showToast } = useAppContext();

  const { data: loyaltyPointDashboardData, isLoading: loyaltyDataLoading } = useSelector(
    (state) => state.loyaltyPointDashboard,
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const [userStartDateFilter, setUserStartDateFilter] = useState('');
  const [userEndDateFilter, setUserEndDateFilter] = useState('');

  const startDateThisYear = new Date().getFullYear();
  const thisDate = format(new Date(), 'yyyy-MM-dd');

  const earned = loyaltyPointDashboardData?.summary?.monthly_trends?.map((item) => item.points_earned);
  const redeemed = loyaltyPointDashboardData?.summary?.monthly_trends?.map((item) => item.points_redeemed);
  const active = loyaltyPointDashboardData?.summary?.monthly_trends?.map((item) => item.points_active);

  // ✅ Hitung nilai maksimum untuk stepSize
  const allValues = earned && redeemed && active ? [...earned, ...redeemed, ...active] : [];
  const maxValue = Math.max(...allValues);
  const stepSize = Math.ceil(maxValue / 8);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: false,
      },
      legend: {
        display: false,
      },
      tooltip: {
        displayColors: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 8,
            weight: 600,
          },
        },
        border: {
          display: false,
        },
      },
      y: {
        grid: {
          display: true,
        },
        ticks: {
          stepSize: stepSize,
          font: {
            size: 8,
            weight: 600,
          },
        },
        border: {
          display: false,
        },
      },
    },
  };

  const data = {
    labels: [
      'Januari',
      'Februari',
      'Maret',
      'April',
      'Mei',
      'Juni',
      'Juli',
      'Agustus',
      'September',
      'Oktober',
      'November',
      'Desember',
    ],
    datasets: [
      {
        data: loyaltyPointDashboardData?.summary?.monthly_trends?.map((item) => item.points_active),
        borderColor: '#009EF7',
      },
      {
        data: loyaltyPointDashboardData?.summary?.monthly_trends?.map((item) => item.points_redeemed),
        borderColor: '#299B0A',
      },
      {
        data: loyaltyPointDashboardData?.summary?.monthly_trends?.map((item) => item.points_earned),
        borderColor: '#F1A33A',
      },
    ],
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setCurrentPage(1);
      dispatch(
        getLoyaltyPointDashboard({
          page: 1,
          limit: 10,
          userStartDate: userStartDateFilter ? userStartDateFilter : `${startDateThisYear}-01-01`,
          userEndDate: userEndDateFilter ? userEndDateFilter : thisDate,
          summaryStartDate: `${startDateThisYear}-01-01`,
          summaryEndDate: thisDate,
          search: searchValue,
        }),
      );
    }, 300);

    return () => clearTimeout(timeout);
  }, [searchValue]);

  const changePageHandler = (newPage) => {
    setCurrentPage(newPage);

    dispatch(
      getLoyaltyPointDashboard({
        page: newPage,
        limit: 10,
        userStartDate: userStartDateFilter ? userStartDateFilter : `${startDateThisYear}-01-01`,
        userEndDate: userEndDateFilter ? userEndDateFilter : thisDate,
        summaryStartDate: `${startDateThisYear}-01-01`,
        summaryEndDate: thisDate,
        search: searchValue,
      }),
    );
  };

  const selectDateHandler = (startDate, endDate) => {
    const formattedStartDate = startDate ? format(new Date(startDate), 'yyyy-MM-dd') : '';
    const formattedEndDate = startDate ? format(new Date(endDate), 'yyyy-MM-dd') : '';

    setUserStartDateFilter(formattedStartDate);
    setUserEndDateFilter(formattedEndDate);

    setCurrentPage(1);
    dispatch(
      getLoyaltyPointDashboard({
        page: 1,
        limit: 10,
        userStartDate: formattedStartDate ? formattedStartDate : `${startDateThisYear}-01-01`,
        userEndDate: formattedEndDate ? formattedEndDate : thisDate,
        summaryStartDate: `${startDateThisYear}-01-01`,
        summaryEndDate: thisDate,
        search: searchValue,
      }),
    );
  };

  const exportToExcelHandler = async () => {
    try {
      await dispatch(
        exportUserLoyaltyPointDashboard({
          userStartDate: userStartDateFilter ? userStartDateFilter : `${startDateThisYear}-01-01`,
          userEndDate: userEndDateFilter ? userEndDateFilter : thisDate,
          search: searchValue,
        }),
      ).unwrap();
    } catch (error) {
      showToast({ type: 'error', message: 'Gagal download user loyalty reports!' });
    }
  };

  const userDetailHandler = (e) => {
    const userId = e.target.parentElement.parentElement.dataset.userid;
    navigate(`/user-customer/detail/${userId}`);
  };

  const downloadReportPdfHandler = async () => {
    navigate('/print-loyalty-point-report');
    const loyaltyDataStorage = {
      download_allowed: true,
      loyalty_report_data: loyaltyPointDashboardData.summary,
      chart_data: data,
      chart_options: options,
      report_period: { start: startDateThisYear, end: thisDate },
      loyalty_isLoading: loyaltyDataLoading,
    };

    localStorage.setItem('loyalty-data-storage', JSON.stringify(loyaltyDataStorage));
  };

  return (
    <div className="dashboard-point-component">
      <div className="dashboard-point-component__title">
        <PointIcon />
        <h1>Dashboard Point</h1>
      </div>

      <div className="dashboard-point-component__point-summary">
        <div className="dashboard-point-component__point-summary__card">
          <h2>Total Point</h2>
          <div className="point-transaction">
            <PointIcon width={20} />
            {loyaltyDataLoading ? (
              <div className="skeleton-loading-card" />
            ) : (
              <p>
                {loyaltyPointDashboardData?.summary?.total_points_active
                  ? setCurrency(loyaltyPointDashboardData?.summary?.total_points_active).replace('Rp', '')
                  : 0}
              </p>
            )}
          </div>
        </div>
        <div className="dashboard-point-component__point-summary__card">
          <h2>Point Terpakai</h2>
          <div className="point-transaction">
            <PointGreenIcon />
            {loyaltyDataLoading ? (
              <div className="skeleton-loading-card" />
            ) : (
              <p>
                {loyaltyPointDashboardData?.summary?.total_points_redeemed
                  ? setCurrency(loyaltyPointDashboardData?.summary?.total_points_redeemed).replace('Rp', '')
                  : 0}
              </p>
            )}
          </div>
        </div>
        <div className="dashboard-point-component__point-summary__card">
          <h2>Point Belum Dipakai</h2>
          <div className="point-transaction">
            <PointOrangeIcon />
            {loyaltyDataLoading ? (
              <div className="skeleton-loading-card" />
            ) : (
              <p>
                {loyaltyPointDashboardData?.summary?.total_points_earned
                  ? setCurrency(loyaltyPointDashboardData?.summary?.total_points_earned).replace('Rp', '')
                  : 0}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="dashboard-point-component__chart">
        <div className="dashboard-point-component__chart__header">
          <h2>Total Point</h2>
          <Button className="export-file" onClick={downloadReportPdfHandler}>
            <DownloadIcon className="download-icon" />
            <span>Export To PDF</span>
          </Button>
        </div>
        <div className="dashboard-point-component__chart__content">
          {loyaltyDataLoading ? <div className="chart-skeleton" /> : <Line data={data} options={options} />}
        </div>
      </div>

      <TableWrapper
        title="Tabel User"
        icon={<PeopleIcon fill="#FF9900" width="25px" height="25px" />}
        className="dashboard-point-component__table-user"
        filter
        search={searchValue}
        setSearch={setSearchValue}
        searchPlaceholder="Cari ..."
        CustomFilterComponent={
          <DateFilter
            placeholder="Pilih Tanggal"
            value="05-04-2025"
            style={{ width: '80%' }}
            selectedDate={selectDateHandler}
          />
        }
        exportToExcel={
          <Button
            type="button"
            style={{
              background: '#F1A33A',
              width: '80%',
            }}
            height={30}
            onClick={exportToExcelHandler}
          >
            <DownloadIcon className="download-icon" />
            <span>Export To Excel</span>
          </Button>
        }
      >
        {loyaltyDataLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            <Table
              column={column}
              actionBtn
              onDetail={userDetailHandler}
              data={loyaltyPointDashboardData?.users}
              currentPage={currentPage}
            />
            <PaginationTable
              currentPage={currentPage}
              onPageChange={changePageHandler}
              totalCount={loyaltyPointDashboardData?.pagination?.total || 0}
            />
          </>
        )}
      </TableWrapper>
    </div>
  );
};

export default DashboardPointComponent;
